import useMergedRef from '@react-hook/merged-ref';
import {
  getPreventDefaultHandler,
  PortalBody,
  usePlateEditorRef,
  usePlateId,
} from '@udecode/plate';
import cn from 'classnames';
import { motion, Variants } from 'framer-motion';
import * as React from 'react';
import { usePopper } from 'react-popper';
import { ReactEditor } from 'slate-react';

import {
  ComboboxProps,
  useComboboxControls,
  useComboboxIsOpen,
  useComboBoxStore,
} from '../combobox';

const { useMemo, useEffect, useCallback } = React;

const virtualReference = {
  getBoundingClientRect() {
    return {
      top: 10,
      left: 10,
      bottom: 20,
      right: 100,
      width: 90,
      height: 10,
    };
  },
};

export const Combobox = ({
  onSelectItem,
  RootWrapper,
  onRenderItem,
}: ComboboxProps): JSX.Element => {
  const at = useComboBoxStore((state) => state.targetRange);
  const items = useComboBoxStore((state) => state.items);
  const itemIndex = useComboBoxStore((state) => state.itemIndex);
  const setItemIndex = useComboBoxStore((state) => state.setItemIndex);
  const combobox = useComboboxControls();
  const isOpen = useComboboxIsOpen();
  const ref = React.useRef<any>(null);

  const menuProps = combobox ? combobox.getMenuProps() : { ref: null };

  const multiRef = useMergedRef(menuProps.ref, ref);

  const { styles, attributes, update } = usePopper(virtualReference as any, ref.current, {
    placement: 'bottom-start',

    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 20],
        },
      },
      { name: 'eventListeners', options: { scroll: !isOpen } },
    ],
  });

  const editor: any = usePlateEditorRef(usePlateId() || undefined);

  const setPosition = useCallback(() => {
    if (at) {
      const domRange = ReactEditor.toDOMRange(editor, at);
      const rect = domRange.getBoundingClientRect();

      virtualReference.getBoundingClientRect = () => rect;
      update?.();
    }
  }, [virtualReference, update, at, editor]);

  useEffect(() => {
    ref && at && setPosition();
  }, [at, editor]);

  useEffect(() => {
    const elem = document.getElementById('document-wrapper') || document;
    elem.addEventListener('scroll', setPosition);
    return () => elem.removeEventListener('scroll', setPosition);
  }, [setPosition]);

  if (!combobox) return <></>;

  const WrapperComp = RootWrapper || React.Fragment;

  const animVariants: Variants = useMemo(() => {
    return {
      enter: {
        visibility: 'visible',
        opacity: 1,
        scale: 1,
        transition: {
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1],
        },
      },
      exit: {
        transitionEnd: {
          visibility: 'hidden',
        },
        opacity: 0,
        scale: 0.8,
        transition: {
          duration: 0.1,
          easings: 'easeOut',
        },
      },
    };
  }, []);

  return (
    <PortalBody>
      <div
        {...menuProps}
        {...attributes.popper}
        style={styles.popper}
        ref={multiRef}
        className={cn({
          '-top-63 -left-63 absolute shadow-sm rounded-lg overflow-hidden bg-white z-2000': isOpen,
          weapons: true,
        })}
      >
        <motion.div variants={animVariants} animate={isOpen && items.length ? 'enter' : 'exit'}>
          {isOpen && items.length ? (
            <WrapperComp>
              <ul>
                {items.map((item, index) => {
                  const Item = onRenderItem
                    ? onRenderItem({ item, highlighted: index === itemIndex })
                    : item.text;
                  return (
                    <li
                      key={item.key}
                      {...combobox.getItemProps({
                        item,
                        index,
                      })}
                      onMouseEnter={() => setItemIndex(index)}
                      onMouseDown={getPreventDefaultHandler(onSelectItem, editor, item)}
                    >
                      {Item}
                    </li>
                  );
                })}
              </ul>
            </WrapperComp>
          ) : null}
        </motion.div>
      </div>
    </PortalBody>
  );
};
